/**
 * Hook para cálculos de métricas de leads
 * 
 * Responsabilidade: Processar e agregar dados de leads para visualizações
 * Princípios: Single Responsibility, DRY
 */

import { useMemo } from 'react';
import { Lead } from '../../../types';
import { DateRange } from '../../../components/shared/filters/DateRangePicker';

export const useLeadsMetrics = (leads: Lead[], dateRange: DateRange) => {
    // Filtra leads pelo período selecionado
    const filteredLeads = useMemo(() => {
        if (!dateRange.start || !dateRange.end) return leads;
        const start = dateRange.start.getTime();
        const end = dateRange.end.getTime();

        return leads.filter(lead => {
            const entry = new Date(lead.entryDate).getTime();
            return entry >= start && entry <= end;
        });
    }, [leads, dateRange]);

    // Leads por dia (para gráfico de período)
    const leadsByDay = useMemo(() => {
        if (!dateRange.start || !dateRange.end) return [];

        const days: { name: string, Negociações: number, rawDate: number }[] = [];
        let curr = new Date(dateRange.start);
        curr.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.end);
        end.setHours(0, 0, 0, 0);

        let count = 0;
        while (curr <= end && count < 366) {
            days.push({
                name: curr.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                Negociações: 0,
                rawDate: curr.getTime()
            });
            curr.setDate(curr.getDate() + 1);
            count++;
        }

        filteredLeads.forEach(lead => {
            const leadDate = new Date(lead.entryDate);
            leadDate.setHours(0, 0, 0, 0);
            const time = leadDate.getTime();
            const found = days.find(d => d.rawDate === time);
            if (found) {
                found.Negociações++;
            }
        });

        return days.map(d => ({ name: d.name, Negociações: d.Negociações }));
    }, [filteredLeads, dateRange]);

    // Leads por status
    const leadsByStatus = useMemo(() => {
        let won = 0;
        let lost = 0;
        let open = 0;

        filteredLeads.forEach(lead => {
            if (lead.status === 'won') won++;
            else if (lead.status === 'lost') lost++;
            else open++;
        });

        return [
            { name: 'Ganho', value: won, color: 'var(--color-success)' },
            { name: 'Perdido', value: lost, color: 'var(--color-danger)' },
            { name: 'Em Aberto', value: open, color: 'var(--color-primary)' }
        ].filter(item => item.value > 0);
    }, [filteredLeads]);

    return {
        filteredLeads,
        leadsByDay,
        leadsByStatus
    };
};
